// src/services/inventoryService.ts
import apiClient from '../config/api';
import { 
  InventoryItem,
  InventoryStats,
  GetItemsResponse,
  ApiResponse 
} from '../types/inventory';

class InventoryService {
  static async addItem(batchId: string, tagCode: string, upc: string, count = 1): Promise<InventoryItem> {
    const response = await apiClient.post<ApiResponse<InventoryItem>>('/inventory/items', {
      batchId: batchId.trim(),
      tagCode: tagCode.trim(),
      upc: upc.trim(),
      count,
    });
    return response.data.data;
  }

  static async updateCount(batchId: string, tagCode: string, upc: string, count: number): Promise<InventoryItem> {
    const response = await apiClient.put<ApiResponse<InventoryItem>>('/inventory/items/count', {
      batchId: batchId.trim(),
      tagCode: tagCode.trim(),
      upc: upc.trim(),
      count,
    });
    return response.data.data;
  }

  static async updateItemMatchingLastTag(batchId: string, tagCode: string, upc: string, count: number): Promise<InventoryItem> {
    const response = await apiClient.put<ApiResponse<InventoryItem>>('/inventory/items/updateItemMatchingLastTag', {
      batchId: batchId.trim(),
      tagCode: tagCode.trim(),
      upc: upc.trim(),
      count,
    });
    return response.data.data;
  }

  static async deleteItem(batchId: string, tagCode: string, upc: string): Promise<{ success: boolean }> {
    const response = await apiClient.delete<ApiResponse<{ success: boolean }>>(
      `/inventory/items/${encodeURIComponent(batchId)}/${encodeURIComponent(tagCode)}/${encodeURIComponent(upc)}`
    );
    return response.data.data;
  }

  // This method returns the FULL API response (including success and data wrapper)
  static async getItems(
    batchId: string,
    page = 1,
    limit = 20,
    search = ''
  ): Promise<GetItemsResponse> {
    const params = new URLSearchParams({
      batchId: batchId.toString(),
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search.trim()) {
      params.append('search', search.trim());
    }

    const response = await apiClient.get<GetItemsResponse>(`/inventory/items?${params.toString()}`);
    return response.data; // Returns the full structure: {success: true, data: {items: [...], pagination: {...}}}
  }

  static async getCurrentStats(batchId: string, tagCode: string): Promise<InventoryStats> {
    const response = await apiClient.get<ApiResponse<InventoryStats>>(
      `/inventory/stats/${encodeURIComponent(batchId)}/${encodeURIComponent(tagCode)}`
    );
    return response.data.data;
  }

  static async exportCsv(batchId: string): Promise<{ success: boolean }> {
    const response = await apiClient.get(`/inventory/export/csv?batchId=${encodeURIComponent(batchId)}`, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `inventory_${batchId}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
  }

  // Get all available batches
  static async getBatches(page: number = 1, limit: number = 30): Promise<{
    data: { id: string; createdAt: string }[],
    pagination: {
      currentPage: number;
      totalPages: number;
      totalCount: number;
      limit: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    }
  }> {
    const response = await apiClient.get<{
      success: boolean;
      data: { id: string; createdAt: string }[];
      pagination: {
        currentPage: number;
        totalPages: number;
        totalCount: number;
        limit: number;
        hasNextPage: boolean;
        hasPrevPage: boolean;
      }
    }>(`/inventory/batches?page=${page}&limit=${limit}`);
    
    return {
      data: response.data.data,
      pagination: response.data.pagination
    };
  }

}

export default InventoryService;